import { computed, signal } from "@preact/signals-core";
import { ApiRequestError } from "../lib/api";
import { bindErrorText, html, list } from "../lib/bind";
import { formatDateTime } from "../lib/date";
import { currentScope } from "../lib/scope";
import { labelRow } from "../lib/table";
import { type AuditLogEntry, listAuditLog } from "../services/auditLog";
import { breadcrumb } from "../stores/breadcrumb";

const AUDIT_LOG_PAGE_SIZE = 50;

// Only the entity_types actually written by app/services/audit_service.py call sites
// (invoice_service, email_service, vat_service, transfer_service/reconciliation_service,
// payment_service, loan_service) — extend this alongside any new audit_service.write() site.
const ENTITY_TYPE_LABELS: Record<string, string> = {
  invoice: "Invoice",
  payment: "Payment",
  vat_period: "VAT period",
  bank_transaction: "Bank transaction",
  loan: "Loan",
};

function entityTypeLabel(entityType: string): string {
  return ENTITY_TYPE_LABELS[entityType] ?? entityType;
}

/** One-line "field: value" (or "field: before → after" when it changed) summary of the
 * row's jsonb snapshot(s) — enough context to read the trail without a raw-JSON dump. */
function formatSnapshot(entry: AuditLogEntry): string {
  const keys = new Set([
    ...Object.keys(entry.before ?? {}),
    ...Object.keys(entry.after ?? {}),
  ]);
  const parts: string[] = [];
  for (const key of keys) {
    const beforeVal = entry.before?.[key];
    const afterVal = entry.after?.[key];
    const hasBefore = entry.before != null && key in entry.before;
    const hasAfter = entry.after != null && key in entry.after;
    if (hasBefore && hasAfter && JSON.stringify(beforeVal) !== JSON.stringify(afterVal)) {
      parts.push(`${key}: ${String(beforeVal)} → ${String(afterVal)}`);
    } else if (hasAfter) {
      parts.push(`${key}: ${String(afterVal)}`);
    } else if (hasBefore) {
      parts.push(`${key}: ${String(beforeVal)}`);
    }
  }
  return parts.join(", ");
}

export function auditLogView(): Node {
  const entries = signal<AuditLogEntry[]>([]);
  const loadError = signal("");

  const entityTypeFilter = signal("");
  const actionFilter = signal("");
  const dateFromFilter = signal("");
  const dateToFilter = signal("");
  const page = signal(1);
  const total = signal(0);

  breadcrumb.value = { label: "Settings", path: "/settings" };
  currentScope().cleanup(() => {
    breadcrumb.value = null;
  });

  function reload(): void {
    listAuditLog({
      entity_type: entityTypeFilter.value || undefined,
      action: actionFilter.value || undefined,
      date_from: dateFromFilter.value || undefined,
      date_to: dateToFilter.value || undefined,
      page: page.value,
      page_size: AUDIT_LOG_PAGE_SIZE,
    })
      .then((result) => {
        entries.value = result.items;
        total.value = result.total;
      })
      .catch((err: unknown) => {
        loadError.value = err instanceof ApiRequestError ? err.body.message : "Failed to load";
      });
  }

  function filtersChanged(): void {
    page.value = 1;
    reload();
  }

  reload();

  const fragment = html`<div class="view">
    <div class="card filters-card">
      <div class="form-row">
        <div class="form-field">
          <label for="audit-filter-entity-type">Entity type</label>
          <select id="audit-filter-entity-type">
            <option value="">All</option>
            <option value="invoice">Invoice</option>
            <option value="payment">Payment</option>
            <option value="vat_period">VAT period</option>
            <option value="bank_transaction">Bank transaction</option>
            <option value="loan">Loan</option>
          </select>
        </div>
        <div class="form-field">
          <label for="audit-filter-action">Action</label>
          <input id="audit-filter-action" placeholder="e.g. invoice.issued" />
        </div>
        <div class="form-field">
          <label for="audit-filter-from">From</label>
          <input id="audit-filter-from" type="date" />
        </div>
        <div class="form-field">
          <label for="audit-filter-to">To</label>
          <input id="audit-filter-to" type="date" />
        </div>
      </div>
    </div>

    <p class="form-error"></p>

    <div class="card">
      <div class="card-label-row">
        <div class="card-label">Audit log</div>
      </div>
      <div class="data-table">
        <table>
          <thead>
            <tr>
              <th>When</th>
              <th>Action</th>
              <th>Entity</th>
              <th>Details</th>
            </tr>
          </thead>
          <tbody class="audit-log-body"></tbody>
        </table>
      </div>

      <div class="pager">
        <button class="btn pager-prev" type="button">Previous</button>
        <span class="pager-label"></span>
        <button class="btn pager-next" type="button">Next</button>
      </div>
    </div>
  </div>`;

  const errorEl = fragment.querySelector<HTMLParagraphElement>(".form-error");
  const table = fragment.querySelector<HTMLTableElement>(".data-table table");
  const tbody = fragment.querySelector<HTMLTableSectionElement>(".audit-log-body");
  const entityTypeSelect = fragment.querySelector<HTMLSelectElement>("#audit-filter-entity-type");
  const actionInput = fragment.querySelector<HTMLInputElement>("#audit-filter-action");
  const fromInput = fragment.querySelector<HTMLInputElement>("#audit-filter-from");
  const toInput = fragment.querySelector<HTMLInputElement>("#audit-filter-to");
  const pagerEl = fragment.querySelector<HTMLElement>(".pager");
  const pagerPrevBtn = fragment.querySelector<HTMLButtonElement>(".pager-prev");
  const pagerNextBtn = fragment.querySelector<HTMLButtonElement>(".pager-next");
  const pagerLabelEl = fragment.querySelector<HTMLElement>(".pager-label");

  if (
    !errorEl ||
    !table ||
    !tbody ||
    !entityTypeSelect ||
    !actionInput ||
    !fromInput ||
    !toInput ||
    !pagerEl ||
    !pagerPrevBtn ||
    !pagerNextBtn ||
    !pagerLabelEl
  ) {
    throw new Error("audit log view markup missing expected elements");
  }

  bindErrorText(errorEl, loadError);

  entityTypeSelect.addEventListener("change", () => {
    entityTypeFilter.value = entityTypeSelect.value;
    filtersChanged();
  });
  let actionDebounce: ReturnType<typeof setTimeout> | undefined;
  actionInput.addEventListener("input", () => {
    actionFilter.value = actionInput.value;
    clearTimeout(actionDebounce);
    actionDebounce = setTimeout(filtersChanged, 300);
  });
  fromInput.addEventListener("change", () => {
    dateFromFilter.value = fromInput.value;
    filtersChanged();
  });
  toInput.addEventListener("change", () => {
    dateToFilter.value = toInput.value;
    filtersChanged();
  });

  const pageCount = computed(() => Math.max(1, Math.ceil(total.value / AUDIT_LOG_PAGE_SIZE)));
  currentScope().effect(() => {
    pagerEl.style.display = pageCount.value > 1 ? "" : "none";
    pagerLabelEl.textContent = `Page ${String(page.value)} of ${String(pageCount.value)}`;
    pagerPrevBtn.disabled = page.value <= 1;
    pagerNextBtn.disabled = page.value >= pageCount.value;
  });
  pagerPrevBtn.addEventListener("click", () => {
    if (page.value > 1) {
      page.value -= 1;
      reload();
    }
  });
  pagerNextBtn.addEventListener("click", () => {
    if (page.value < pageCount.value) {
      page.value += 1;
      reload();
    }
  });

  list(
    tbody,
    entries,
    (entry) => entry.id,
    (entry) => {
      const row = html`<tr>
        <td>${formatDateTime(entry.at)}</td>
        <td>${entry.action}</td>
        <td>${entityTypeLabel(entry.entity_type)}</td>
        <td class="audit-log-details">${formatSnapshot(entry)}</td>
      </tr>`.firstElementChild;
      if (!row) throw new Error("audit log row template produced no element");
      return labelRow(row, table);
    },
  );

  return fragment;
}
